import { useEffect, useState, useCallback } from 'react';
import { X, Copy, Check, FileCode, Eye, Code2, RotateCcw, ToggleLeft, ToggleRight, Users, User } from 'lucide-react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import { api } from '../api/client';
import './PromptInspectorModal.css';

// Module-scope plugin tuples — same trick as AgentDetail / MultiAgent /
// QueueChat: react-markdown memoises against array identity, so freezing
// these prevents a re-parse on every parent render tick.
const REMARK_PLUGINS = [remarkGfm, remarkMath];
const REHYPE_PLUGINS = [rehypeKatex];

interface Props {
  sessionName: string;
  /** Pool the bot belongs to, if any. When present, the inspector shows a
   *  scope switch (Bot vs Pool) so toggles can apply pool-wide. Empty /
   *  undefined => bot is standalone, only per-bot scope is offered. */
  poolName?: string;
  onClose: () => void;
}

type Layer = {
  source: string;
  path: string;
  content: string;
  section?: string;
  title?: string;
  scope_type?: string;
  scope_key?: string;
  post_id?: number;
  canonical_key?: string;
  disabled?: boolean;
  disabled_by?: 'bot' | 'pool';
};
type PromptResp = {
  name: string;
  agent: string;
  instruction_file: string;
  rendered: string;
  // Cloud's ef40eb9: three separate lists. disabled_keys is the merged
  // union, the scoped variants drive per-scope sidebar state.
  disabled_keys?: string[];
  bot_disabled_keys?: string[];
  pool_disabled_keys?: string[];
  pool_name?: string;
  layers: Layer[];
};
type PoolPromptResp = {
  name: string;
  pool_name?: string;
  pool_disabled_keys?: string[];
  disabled_keys?: string[];
  layers: Layer[];
};

type Scope = 'bot' | 'pool';
type MemoryFile = { name: string; size: number; content: string; is_index?: boolean };
type MemoryProject = { project_id: string; files: MemoryFile[] };
type MemoryResp = { name: string; projects: MemoryProject[] };

// Tagged union for the active sidebar selection. Was `number | 'all'`
// before the memory feature; now also supports memory-file selection
// with project+filename addressing so multi-project bots are handled.
type Active =
  | { kind: 'rendered' }
  | { kind: 'layer'; i: number }
  | { kind: 'memory'; projectId: string; filename: string };

const SOURCE_LABELS: Record<string, string> = {
  identity_block: 'Identity',
  // Hierarchical fragments (Cloud commit 51494c5, IACP 96b1707d).
  // Authored as content-api posts with doc_type=prompt_fragment and
  // a scope tag — system, mcp, node, tenant, pool, or bot. Backend
  // assembles them via _compose_persona between the identity block
  // and the legacy bot-dir file.
  'system-fragment': 'System',
  'mcp-fragment': 'MCP',
  'node-fragment': 'Node',
  'tenant-fragment': 'Tenant',
  'pool-fragment': 'Pool',
  'bot-fragment': 'Bot',
  'dept-fragment': 'Department',
  // Forward-compat aliases — same payload, older naming attempt.
  // Kept so a transient backend response doesn't render as raw slug.
  system_layer: 'System',
  tenant_layer: 'Tenant',
  // Legacy disk-backed templates.
  'per-bot-template': 'Per-Bot Template',
  'pool-template': 'Pool Template',
  'bot-dir-file': 'Bot-Dir File',
  'host-fallback': 'Host Fallback',
};

function sizeKB(text: string): string {
  // Both KB and char count — chars is what Claude Code's 40k-char limit
  // is measured in (see startup warning for over-limit bots), so the
  // sidebar lets you see at a glance which fragments push you toward
  // the budget. Format compactly so the column doesn't blow up.
  const b = new Blob([text]).size;
  const chars = text.length;
  const charsLabel = chars >= 1000
    ? `${(chars / 1000).toFixed(1)}k chars`
    : `${chars} chars`;
  if (b < 1024) return `${b} B · ${charsLabel}`;
  return `${(b / 1024).toFixed(1)} KB · ${charsLabel}`;
}

export function PromptInspectorModal({ sessionName, poolName, onClose }: Props) {
  const [data, setData] = useState<PromptResp | null>(null);
  const [poolData, setPoolData] = useState<PoolPromptResp | null>(null);
  const [memory, setMemory] = useState<MemoryResp | null>(null);
  const [error, setError] = useState('');
  const [active, setActive] = useState<Active>({ kind: 'rendered' });
  const [copied, setCopied] = useState(false);
  // Scope determines which `disabled_keys` set drives the per-row toggle
  // visualization AND which API endpoint a toggle click hits. Defaults to
  // 'bot' since most agents are standalone — Pool tab only appears when
  // `poolName` is set on the open caller.
  const [scope, setScope] = useState<Scope>('bot');
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [saveError, setSaveError] = useState('');
  // Which fragment-source groups are collapsed in the sidebar. Default
  // is all expanded so the user sees the full pipeline; clicking a
  // group header toggles. Not persisted — modal-session-local.
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  // Default to rendered Markdown — agents read CLAUDE.md / AGENTS.md /
  // GEMINI.md as plain text, but for a human inspecting the prompt the
  // raw markup is harder than the formatted version. The Raw toggle is
  // there for when you really want to see exact bytes (copy, diff, …).
  const [view, setView] = useState<'rendered' | 'raw'>('rendered');

  const reload = useCallback(() => {
    let cancelled = false;
    api.sessions.effectivePrompt(sessionName)
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : 'Load failed'); });
    // Pool data — only when bot is in a pool. Failure here is silent
    // (pool view simply won't have its own list; falls back to per-bot
    // view if backend hasn't shipped the pool endpoint yet).
    if (poolName) {
      api.pools.effectivePrompt(poolName)
        .then((p) => { if (!cancelled) setPoolData(p); })
        .catch(() => { /* backend may not have endpoint yet — soft fail */ });
    }
    return () => { cancelled = true; };
  }, [sessionName, poolName]);

  useEffect(() => {
    const cleanup = reload();
    // Memory is fetched in parallel — bot's private learning. Failure
    // is silent (bot might not have any memory yet); only the prompt
    // fetch's failure surfaces as a hard error.
    let cancelled = false;
    api.sessions.memory(sessionName)
      .then((m) => { if (!cancelled) setMemory(m); })
      .catch(() => { /* no memory dir — leave sidebar section hidden */ });
    return () => { cancelled = true; cleanup(); };
  }, [sessionName, reload]);

  // Set of canonical_keys disabled in the ACTIVE scope. Cloud's ef40eb9
  // gives us `bot_disabled_keys` and `pool_disabled_keys` as separate
  // arrays at top-level — pick the right one based on the scope tab.
  // Fallback to per-layer `disabled` + `disabled_by` if the backend
  // hasn't shipped the array yet (forward-compat).
  const disabledInScope: Set<string> = (() => {
    if (scope === 'pool') {
      // Cloud's pool endpoint federates and may surface the pool-scope
      // list under `disabled_keys` (because that's the only kind for a
      // pool endpoint) OR under the explicit `pool_disabled_keys`. The
      // bot endpoint always uses the explicit scoped field. Probe both
      // in priority order, then fall back to per-layer ghost flag.
      const fromPool =
        poolData?.pool_disabled_keys
        ?? data?.pool_disabled_keys
        ?? poolData?.disabled_keys;
      if (fromPool) return new Set(fromPool);
      const layers = poolData?.layers || data?.layers || [];
      return new Set(
        layers
          .filter((l) => l.disabled && l.canonical_key && (l.disabled_by ?? 'pool') === 'pool')
          .map((l) => l.canonical_key!),
      );
    }
    if (data?.bot_disabled_keys) return new Set(data.bot_disabled_keys);
    return new Set(
      (data?.layers || [])
        .filter((l) => l.disabled && l.canonical_key && (l.disabled_by ?? 'bot') === 'bot')
        .map((l) => l.canonical_key!),
    );
  })();

  const toggleFragment = async (canonicalKey: string) => {
    if (savingKey) return; // serialize concurrent toggles
    setSavingKey(canonicalKey);
    setSaveError('');
    const currentlyDisabled = disabledInScope.has(canonicalKey);
    const next = new Set(disabledInScope);
    if (currentlyDisabled) next.delete(canonicalKey);
    else next.add(canonicalKey);
    const nextArray = Array.from(next).sort();
    try {
      if (scope === 'pool' && poolName) {
        await api.pools.setDisabledFragments(poolName, nextArray);
        // Optimistic local write into the SCOPED field so the toggle
        // visual flips instantly. Cloud also returns the merged set as
        // `disabled_keys`; we don't reconstruct that locally because the
        // UI reads only the scoped value (see disabledInScope above).
        setPoolData((p) => p ? { ...p, pool_disabled_keys: nextArray } : p);
        setData((d) => d ? { ...d, pool_disabled_keys: nextArray } : d);
      } else {
        await api.sessions.setDisabledFragments(sessionName, nextArray);
        setData((d) => d ? { ...d, bot_disabled_keys: nextArray } : d);
      }
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Toggle failed');
    } finally {
      setSavingKey(null);
    }
  };

  const resetScope = async () => {
    if (savingKey) return;
    setSavingKey('__reset__');
    setSaveError('');
    try {
      if (scope === 'pool' && poolName) {
        await api.pools.setDisabledFragments(poolName, []);
        setPoolData((p) => p ? { ...p, pool_disabled_keys: [] } : p);
        setData((d) => d ? { ...d, pool_disabled_keys: [] } : d);
      } else {
        await api.sessions.setDisabledFragments(sessionName, []);
        setData((d) => d ? { ...d, bot_disabled_keys: [] } : d);
      }
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Reset failed');
    } finally {
      setSavingKey(null);
    }
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Resolve the currently-active selection to its raw text. Returns ''
  // when the source isn't loaded yet — the JSX guards with !data already,
  // but memory rows can be clicked even before the prompt has rendered.
  const resolveShown = (): string => {
    if (active.kind === 'rendered') return data?.rendered || '';
    if (active.kind === 'layer') return data?.layers[active.i]?.content || '';
    if (active.kind === 'memory') {
      const proj = memory?.projects.find((p) => p.project_id === active.projectId);
      return proj?.files.find((f) => f.name === active.filename)?.content || '';
    }
    return '';
  };

  const copy = async () => {
    const text = resolveShown();
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard denied */ }
  };

  const shownText = resolveShown();

  return (
    <>
      <div className="pi-backdrop" onClick={onClose} />
      <div className="pi-modal" role="dialog" aria-label={`Prompt für ${sessionName}`}>
        <header className="pi-header">
          <div className="pi-title">
            <FileCode size={14} />
            <span>{sessionName}</span>
            {data && <span className="pi-meta">{data.instruction_file} · {data.agent}</span>}
          </div>
          <div className="pi-header-actions">
            <button
              type="button"
              className="pi-action"
              onClick={() => setView((v) => (v === 'rendered' ? 'raw' : 'rendered'))}
              title={view === 'rendered' ? 'Raw Markdown anzeigen' : 'Formatiert anzeigen'}
              disabled={!data}
            >
              {view === 'rendered' ? <Code2 size={14} /> : <Eye size={14} />}
            </button>
            <button type="button" className="pi-action" onClick={copy} title="Kopieren" disabled={!data}>
              {copied ? <Check size={14} /> : <Copy size={14} />}
            </button>
            <button type="button" className="pi-close" onClick={onClose} aria-label="Close">
              <X size={16} />
            </button>
          </div>
        </header>

        <div className="pi-body">
          {error && <div className="pi-error">Fehler: {error}</div>}
          {!data && !error && <div className="pi-loading">Lade Prompt…</div>}
          {data && (
            <>
              <aside className="pi-layers">
                {/* Scope-Switch — only when this bot belongs to a pool.
                  * "Bot" scope toggles persist in session_registry, "Pool"
                  * scope toggles persist in pool_config and apply to every
                  * pool member at next prepare_bot_home. */}
                {poolName && (
                  <div className="pi-scope-switch" role="tablist" aria-label="Toggle scope">
                    <button
                      type="button"
                      className={`pi-scope-btn${scope === 'bot' ? ' active' : ''}`}
                      onClick={() => setScope('bot')}
                      title="Toggles wirken nur für diesen Bot"
                    >
                      <User size={11} />
                      <span>Bot</span>
                    </button>
                    <button
                      type="button"
                      className={`pi-scope-btn${scope === 'pool' ? ' active' : ''}`}
                      onClick={() => setScope('pool')}
                      title={`Toggles wirken für alle Mitglieder des Pools "${poolName}"`}
                    >
                      <Users size={11} />
                      <span>Pool: {poolName}</span>
                    </button>
                  </div>
                )}

                {/* Counter + Reset row. Counter reflects ACTIVE scope.
                  * Reset clears that scope's disabled list. */}
                <div className="pi-toggle-bar">
                  <span className="pi-toggle-counter">
                    {data.layers.length - disabledInScope.size} aktiv
                    {disabledInScope.size > 0 && (
                      <> · <span className="pi-toggle-counter-off">{disabledInScope.size} disabled</span></>
                    )}
                  </span>
                  {disabledInScope.size > 0 && (
                    <button
                      type="button"
                      className="pi-toggle-reset"
                      onClick={resetScope}
                      disabled={savingKey === '__reset__'}
                      title={scope === 'pool'
                        ? `Pool "${poolName}" Override zurücksetzen`
                        : 'Bot-Override zurücksetzen'}
                    >
                      <RotateCcw size={11} />
                      reset
                    </button>
                  )}
                </div>

                {saveError && (
                  <div className="pi-toggle-error" role="alert">{saveError}</div>
                )}

                <button
                  type="button"
                  className={`pi-layer-row${active.kind === 'rendered' ? ' active' : ''}`}
                  onClick={() => setActive({ kind: 'rendered' })}
                >
                  <span className="pi-layer-source">Rendered</span>
                  <span className="pi-layer-size">{sizeKB(data.rendered)}</span>
                </button>

                {/* Group layers by source-type so the sidebar mirrors the
                  * compose-pipeline pyramid (Identity → System → MCP →
                  * Department → Node → Tenant → Pool → Bot → legacy).
                  * Within each group rows keep their original layer
                  * index for `active.kind:'layer'` continuity. Each group
                  * is collapsible — header chevron drives the
                  * `collapsedGroups` set. */}
                {(() => {
                  // Canonical render-order matching Cloud's
                  // _compose_persona stack (Post #998).
                  const SOURCE_ORDER = [
                    'identity_block',
                    'system-fragment', 'system_layer',
                    'mcp-fragment',
                    'dept-fragment',
                    'node-fragment',
                    'tenant-fragment', 'tenant_layer',
                    'pool-fragment',
                    'bot-fragment',
                    'per-bot-template', 'pool-template',
                    'bot-dir-file', 'host-fallback',
                  ];
                  // Stable preserve-original-index grouping.
                  const groups = new Map<string, Array<{ l: Layer; i: number }>>();
                  data.layers.forEach((l, i) => {
                    const src = l.source || 'unknown';
                    if (!groups.has(src)) groups.set(src, []);
                    groups.get(src)!.push({ l, i });
                  });
                  const orderedKeys = [
                    ...SOURCE_ORDER.filter((k) => groups.has(k)),
                    ...Array.from(groups.keys()).filter((k) => !SOURCE_ORDER.includes(k)),
                  ];

                  return orderedKeys.map((src) => {
                    const rows = groups.get(src)!;
                    const groupLabel = SOURCE_LABELS[src] || src;
                    const isCollapsed = collapsedGroups.has(src);
                    // Identity = single row, no header noise — render as
                    // bare row at the top. Everything else gets a header.
                    if (src === 'identity_block') {
                      return rows.map(({ l, i }) => (
                        <div
                          key={`identity-${i}`}
                          className={`pi-layer-row${active.kind === 'layer' && active.i === i ? ' active' : ''}`}
                          role="button"
                          tabIndex={0}
                          onClick={() => setActive({ kind: 'layer', i })}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              setActive({ kind: 'layer', i });
                            }
                          }}
                          title={l.title || l.path || ''}
                        >
                          <span className="pi-layer-source">Identity</span>
                          <span className="pi-layer-size">{sizeKB(l.content)}</span>
                        </div>
                      ));
                    }
                    // Group's own disabled-count for the header pill.
                    const groupDisabledCount = rows.filter(
                      ({ l }) => l.canonical_key && disabledInScope.has(l.canonical_key),
                    ).length;
                    return (
                      <div key={`grp-${src}`} className="pi-fragment-group">
                        <button
                          type="button"
                          className={`pi-section-header pi-section-header-toggle${isCollapsed ? ' is-collapsed' : ''}`}
                          onClick={() => {
                            setCollapsedGroups((prev) => {
                              const next = new Set(prev);
                              if (next.has(src)) next.delete(src);
                              else next.add(src);
                              return next;
                            });
                          }}
                          title={isCollapsed ? 'Ausklappen' : 'Einklappen'}
                        >
                          <span className="pi-section-chevron">{isCollapsed ? '▸' : '▾'}</span>
                          <span>{groupLabel}</span>
                          <span className="pi-section-pill">
                            {rows.length}
                            {groupDisabledCount > 0 && ` · ${groupDisabledCount} off`}
                          </span>
                        </button>
                        {!isCollapsed && rows.map(({ l, i }) => {
                          const primary = l.section || groupLabel;
                          const scopeChip = l.section && l.scope_key
                            ? `${groupLabel} · ${l.scope_key}`
                            : '';
                          const subtitle = l.post_id
                            ? `${scopeChip ? scopeChip + ' · ' : ''}#${l.post_id}`
                            : scopeChip || l.path;
                          // Toggle gating — same as before: canonical_key
                          // is the stable per-fragment identifier the
                          // backend persists in disabled_fragments. Some
                          // sources (dept-fragment, mcp-fragment) don't
                          // ship canonical_key yet — those rows render
                          // without the toggle. Flagged to Cloud.
                          const canToggle = !!l.canonical_key;
                          const isDisabled = canToggle && disabledInScope.has(l.canonical_key!);
                          const isSaving = canToggle && savingKey === l.canonical_key;
                          return (
                            <div
                              key={i}
                              className={
                                `pi-layer-row pi-layer-row-nested${active.kind === 'layer' && active.i === i ? ' active' : ''}` +
                                `${isDisabled ? ' is-disabled' : ''}`
                              }
                              role="button"
                              tabIndex={0}
                              onClick={() => setActive({ kind: 'layer', i })}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  setActive({ kind: 'layer', i });
                                }
                              }}
                              title={l.title || l.path || ''}
                            >
                              <span className="pi-layer-source">{primary}</span>
                              <span className="pi-layer-size">{sizeKB(l.content)}</span>
                              {subtitle && <span className="pi-layer-path">{subtitle}</span>}
                              {canToggle && (
                                <button
                                  type="button"
                                  className={`pi-layer-toggle${isDisabled ? ' is-off' : ''}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    void toggleFragment(l.canonical_key!);
                                  }}
                                  disabled={isSaving || !!savingKey}
                                  aria-pressed={!isDisabled}
                                  title={
                                    isDisabled
                                      ? `Aktivieren für ${scope === 'pool' ? `Pool "${poolName}"` : sessionName}`
                                      : `Deaktivieren für ${scope === 'pool' ? `Pool "${poolName}"` : sessionName}`
                                  }
                                >
                                  {isDisabled
                                    ? <ToggleLeft size={16} />
                                    : <ToggleRight size={16} />}
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    );
                  });
                })()}

                {/* Memory section — bot's private learning from the
                  * /auto-memory skill. Separate visual band (lila accent
                  * + "private" pill) so it's clear this is not part of
                  * the federation-derived prompt assembly. */}
                {memory && memory.projects.some((p) => p.files.length > 0) && (
                  <>
                    <div className="pi-section-header">
                      <span>Memory</span>
                      <span className="pi-section-pill">private</span>
                    </div>
                    {memory.projects.map((proj) => (
                      <div key={proj.project_id}>
                        {memory.projects.length > 1 && (
                          <div className="pi-section-subheader" title={proj.project_id}>
                            {proj.project_id === '-' ? '(default)' : proj.project_id}
                          </div>
                        )}
                        {proj.files.map((f) => (
                          <button
                            type="button"
                            key={`${proj.project_id}:${f.name}`}
                            className={`pi-layer-row pi-layer-row-memory${
                              active.kind === 'memory' && active.projectId === proj.project_id && active.filename === f.name
                                ? ' active'
                                : ''
                            }`}
                            onClick={() => setActive({ kind: 'memory', projectId: proj.project_id, filename: f.name })}
                            title={f.name}
                          >
                            <span className="pi-layer-source">
                              {f.name}
                              {f.is_index && <span className="pi-layer-badge">index</span>}
                            </span>
                            <span className="pi-layer-size">{sizeKB(f.content)}</span>
                          </button>
                        ))}
                      </div>
                    ))}
                  </>
                )}
              </aside>
              {view === 'raw' ? (
                <pre className="pi-content pi-content-raw">{shownText}</pre>
              ) : (
                <div className="pi-content pi-content-md">
                  <Markdown
                    remarkPlugins={REMARK_PLUGINS}
                    rehypePlugins={REHYPE_PLUGINS}
                    components={
                      // When viewing a memory file, the MEMORY.md index uses
                      // relative links like `[Title](file.md)` — intercept
                      // those and switch to the sibling memory file instead
                      // of letting the browser navigate. External http(s)
                      // links pass through unchanged.
                      active.kind === 'memory'
                        ? {
                            a: ({ href, children, ...rest }) => {
                              const isRelative = href && !/^https?:\/\//i.test(href) && !href.startsWith('#');
                              if (isRelative && active.kind === 'memory') {
                                const proj = memory?.projects.find((p) => p.project_id === active.projectId);
                                const target = proj?.files.find((f) => f.name === href);
                                if (target) {
                                  return (
                                    <a
                                      href="#"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        setActive({ kind: 'memory', projectId: active.projectId, filename: target.name });
                                      }}
                                      {...rest}
                                    >
                                      {children}
                                    </a>
                                  );
                                }
                              }
                              return <a href={href} {...rest}>{children}</a>;
                            },
                          }
                        : undefined
                    }
                  >
                    {shownText}
                  </Markdown>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}
